import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { SplitText } from "gsap/SplitText"

gsap.registerPlugin(ScrollTrigger, SplitText)

gsap.defaults({ ease: "expo.out", duration: 0.8 })
ScrollTrigger.config({ ignoreMobileResize: true })

// Keep these in sync with the md breakpoint (48rem) in Tailwind.
export const MEDIA = {
  desktop: "(min-width: 768px) and (prefers-reduced-motion: no-preference)",
  mobile: "(max-width: 767.98px) and (prefers-reduced-motion: no-preference)",
  reduce: "(prefers-reduced-motion: reduce)"
}

export const FINE_POINTER = "(hover: hover) and (pointer: fine)"

export const EASE = {
  out: "expo.out",
  soft: "quart.out",
  inOut: "power3.inOut"
}

let refreshFrame = null

// Many controllers create ScrollTriggers in the same tick; batch one sorted refresh so
// pin spacers above a trigger are always accounted for, regardless of connect order.
export function scheduleRefresh() {
  if (refreshFrame) return
  refreshFrame = requestAnimationFrame(() => {
    refreshFrame = null
    ScrollTrigger.sort()
    ScrollTrigger.refresh()
  })
}

let refreshTimer = null

// Trailing-debounced variant for controllers that start lazily, so a burst of idle-time
// starts costs one refresh instead of one per frame.
export function scheduleLazyRefresh() {
  clearTimeout(refreshTimer)
  refreshTimer = setTimeout(scheduleRefresh, 250)
}

// Lazy start: run `start` when the element nears the viewport, or earlier during idle time.
// Off-screen markup keeps its static final state until then, so deferring is invisible.
const pending = new Map()
const idle = window.requestIdleCallback ||
  ((fn) => setTimeout(() => fn({ didTimeout: true, timeRemaining: () => 0 }), 60))
let idleQueued = false

const runPending = (element) => {
  const start = pending.get(element)
  if (!start) return
  pending.delete(element)
  nearObserver.unobserve(element)
  start()
}

const nearObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => { if (entry.isIntersecting) runPending(entry.target) })
}, { rootMargin: "75% 0px" })

const drainIdle = (deadline) => {
  idleQueued = false
  let ran = 0
  for (const element of pending.keys()) {
    if (ran > 0 && deadline.timeRemaining() < 8) break
    runPending(element)
    ran++
  }
  queueIdle()
}

const queueIdle = () => {
  if (idleQueued || pending.size === 0) return
  idleQueued = true
  idle(drainIdle, { timeout: 4000 })
}

export function startWhenNear(element, start) {
  pending.set(element, start)
  nearObserver.observe(element)
  queueIdle()
  return () => {
    pending.delete(element)
    nearObserver.unobserve(element)
  }
}

export function observeVisibility(element, callback, options = { rootMargin: "120px 0px" }) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => callback(entry.isIntersecting))
  }, options)
  observer.observe(element)
  return () => observer.disconnect()
}

if (typeof window !== "undefined" && !window.__fiannaMotion) {
  window.__fiannaMotion = true
  document.addEventListener("turbo:load", scheduleRefresh)
}

export { gsap, ScrollTrigger, SplitText }
